import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  AdvancedMarker,
  APIProvider,
  Map,
  useMap,
  ControlPosition,
  InfoWindow,
  useMapsLibrary,
  MapControl,
} from "@vis.gl/react-google-maps";
import { PinMapFill } from "react-bootstrap-icons";
import { useUserContext } from "../../../context/UserContext";
import {
  Button,
  Spinner,
  Modal,
  Stack,
} from "react-bootstrap";
import { toast } from "../../../utilities/toast";
import {
  fetchOrderById,
  fetchStoreInfo,
  fetchStores,
  updateActiveStatus,
} from "../../../lib/webService/webService";
import {
  fetchProduct,
  fetchOptionItems,
  fetchPlacedOrders,
  fetchActiveOrders,
  acceptOrder,
  fetchActiveDeliveryById,
} from "../../../lib/webService/webService";
import { useNavigate, useLocation as useRouteLocation } from "react-router-dom";
import { goToLogin } from "../../../utilities/authRedirect";
import { useMobileFeatures } from "../../../hooks/useMobileFeatures";
import "./order-styles.css";
import { formatCurrency } from "../../../utilities/formatCurrency";
import { PullToRefreshWrapper } from "../../../components/pullRefresh";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Apple Design System CSS ──────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("ops-main-styles")) {
  const s = document.createElement("style");
  s.id = "ops-main-styles";
  s.textContent = `
    .ops-seg{display:flex;background:rgba(118,118,128,.12);padding:2px;border-radius:9px;gap:0}
    .ops-seg-btn{padding:5px 16px;border:none;border-radius:7px;font-size:.875rem;font-weight:500;cursor:pointer;transition:all .18s;background:transparent;color:rgba(60,60,67,.65);font-family:inherit;display:flex;align-items:center;gap:5px}
    .ops-seg-btn.ops-act{background:#fff;color:#1c1c1e;box-shadow:0 1px 4px rgba(0,0,0,.10)}
    .ops-rfrsh{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border:none;border-radius:10px;font-size:.8125rem;font-weight:500;background:rgba(0,122,255,.08);color:#007AFF;cursor:pointer;transition:opacity .15s;font-family:inherit}
    .ops-rfrsh:disabled{opacity:.5;cursor:not-allowed}
    .ops-card{background:rgba(255,255,255,.88);border:1px solid rgba(15,23,42,.06);border-radius:16px;padding:16px;transition:transform .22s cubic-bezier(.34,1.24,.64,1),box-shadow .22s;cursor:pointer}
    .ops-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.08)!important}
    .ops-card:active{transform:scale(.985)}
    .ops-pill{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:20px;font-size:.75rem;font-weight:600}
    .ops-start{padding:8px 20px;background:#007AFF;color:#fff;border:none;border-radius:10px;font-size:.875rem;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px;transition:opacity .15s;font-family:inherit}
    .ops-start:disabled{opacity:.55;cursor:not-allowed}
    .ops-start:active{opacity:.82}
    .ops-modal .modal-content{border-radius:20px!important;border:none!important;overflow:hidden}
    @keyframes _ops_sk{0%,100%{opacity:1}50%{opacity:.4}}
    ._ops_sk{animation:_ops_sk 1.8s ease-in-out infinite;background:rgba(60,60,67,.08);border-radius:6px}
    .ops-section-lbl{font-size:.72rem;font-weight:600;color:rgba(60,60,67,.55);text-transform:uppercase;letter-spacing:.05em;margin-bottom:10px}
  `;
  document.head.appendChild(s);
}

const OPS_FONT = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;

// Custom Hooks
const useLocation = () => {
  const [location, setLocation] = useState({ lat: 0, lng: 0 });
  const { isNative, getCurrentLocation } = useMobileFeatures();

  const getUserLocation = useCallback(async () => {
    if (isNative) {
      // Use Capacitor's geolocation
      try {
        const coordinates = await getCurrentLocation();
        if (coordinates) {
          setLocation({ 
            lat: coordinates.coords.latitude, 
            lng: coordinates.coords.longitude 
          });
        }
      } catch (error) {
        console.error("Error getting location with Capacitor:", error);
        toast.error("Failed to get your location");
      }
    } else {
      // Fallback to web geolocation
      const options = {
        enableHighAccuracy: true,
      };

      const success = (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ lat: latitude, lng: longitude });
      };

      const error = (error) => {
        console.error("Error getting location:", error);
        toast.error("Failed to get your location");
      };

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(success, error, options);
      } else {
        toast.error("Geolocation is not supported by your browser");
      }
    }
  }, [isNative, getCurrentLocation]);

  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  return location;
};

// Components
const LoadingSpinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
    <div style={{ width: 36, height: 36, border: "3px solid rgba(0,122,255,.18)", borderTopColor: "#007AFF", borderRadius: "50%", animation: "_ops_sk_spin 0.8s linear infinite" }} />
    <style>{`@keyframes _ops_sk_spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const SessionErrorCard = ({ onLoginClick }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "50vh", fontFamily: OPS_FONT }}>
    <div style={{ textAlign: "center", background: "rgba(255,255,255,.88)", border: "1px solid rgba(15,23,42,.06)", borderRadius: 20, padding: "40px 32px", maxWidth: 360, width: "100%" }}>
      <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(255,59,48,.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FF3B30" viewBox="0 0 16 16">
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
          <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
        </svg>
      </div>
      <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 8 }}>Session Expired</div>
      <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 24, lineHeight: 1.5 }}>
        Your session has expired. Please log in again to continue.
      </div>
      <button
        onClick={onLoginClick}
        style={{ padding: "12px 28px", background: "#007AFF", color: "#fff", border: "none", borderRadius: 12, fontSize: ".9375rem", fontWeight: 600, cursor: "pointer", fontFamily: OPS_FONT }}
      >
        Login Again
      </button>
    </div>
  </div>
);

const MapSection = React.memo(({
  userLocation,
  orders,
  setOrders,
  stores,
  setStores,
  setActiveOrders,
  riderId,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { triggerHaptic } = useMobileFeatures();
  const { token } = useUserContext();

  // Memoize filtered orders to prevent unnecessary re-renders
  const filteredOrders = useMemo(() =>
    orders.filter(order => order.paid === true),
    [orders]
  );

  // Memoize refresh function to prevent recreation on every render
  const handleRefresh = useCallback(async () => {
    try {
      await triggerHaptic();
      setIsRefreshing(true);
      setIsLoading(true);
      const [placedOrders, storesData] = await Promise.all([
        fetchPlacedOrders(token),
        fetchStores(),
      ]);
      setOrders(placedOrders);
      setStores(storesData);
    } catch (error) {
      console.error("Error refreshing map data:", error);
      toast.error("Failed to refresh map data");
    } finally {
      setIsRefreshing(false);
      setTimeout(() => setIsLoading(false), 500);
    }
  }, [setOrders, setStores, token]);

  // Optimize loading effect - only run once on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []); // Remove dependencies to prevent unnecessary re-runs

  // Memoize skeleton to prevent recreation
  const mapSkeleton = useMemo(() => (
    <div
      className="d-block rounded position-relative"
      style={{
        height: "264px",
        backgroundColor: "#e5e7eb",
        overflow: "hidden",
      }}
    >
      <div className="skeleton-map">
        <div className="skeleton-marker" style={{ top: "30%", left: "40%" }} />
        <div className="skeleton-marker" style={{ top: "50%", left: "60%" }} />
        <div className="skeleton-marker" style={{ top: "70%", left: "30%" }} />
      </div>
    </div>
  ), []);

  // Memoize map content to prevent unnecessary re-renders
  const mapContent = useMemo(() => (
    <div
      className="d-block rounded"
      style={{ height: "264px", backgroundColor: "#e5e7eb" }}
    >
      <APIProvider apiKey={import.meta.env.VITE_MAP_API_KEY}>
        <Map
          defaultZoom={13}
          defaultCenter={userLocation}
          mapId={import.meta.env.VITE_MAP_ID}
        >
          <PoiMarker
            orders={filteredOrders}
            setOrders={setOrders}
            userLocation={userLocation}
            setActiveOrders={setActiveOrders}
            riderId={riderId}
          />
          {/* <StoreMarker stores={stores} userLocation={userLocation} /> */}
        </Map>
      </APIProvider>
    </div>
  ), [filteredOrders, stores, userLocation, setOrders, setActiveOrders, riderId]);

  return (
    <Stack gap={2}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e" }}>Pick Order</div>
          <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", marginTop: 2 }}>
            Choose orders to deliver from the map below.
          </div>
        </div>
        <button className="ops-rfrsh" onClick={handleRefresh} disabled={isRefreshing || isLoading}>
          {isRefreshing ? (
            <><span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(0,122,255,.3)", borderTopColor: "#007AFF", borderRadius: "50%", animation: "_ops_sk_spin .7s linear infinite" }} />Refreshing...</>
          ) : (
            <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/><path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/></svg>Refresh</>
          )}
        </button>
      </div>
      {isLoading ? mapSkeleton : mapContent}
    </Stack>
  );
});

const ActiveOrdersSection = ({
  activeOrders,
  user,
  riderId,
  orders,
  setOrders,
  setActiveOrders,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { triggerHaptic } = useMobileFeatures();
  const { token } = useUserContext();

  useEffect(() => {
    // Simulate loading time for active orders
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [activeOrders]);

  const handleRefresh = async () => {
    try {
      await triggerHaptic();
      setIsRefreshing(true);
      setIsLoading(true); // Show loading state
      const activeOrdersData = await fetchActiveOrders(riderId, token);
      setActiveOrders(activeOrdersData);
    } catch (error) {
      console.error("Error refreshing orders:", error);
      toast.error("Failed to refresh orders");
    } finally {
      setIsRefreshing(false);
      // Add a small delay before hiding loading state to ensure smooth transition
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  const renderLoadingSkeletons = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[1, 2, 3].map((i) => (
        <div key={i} className="ops-card" style={{ cursor: "default" }}>
          <div className="_ops_sk" style={{ width: "38%", height: 11, marginBottom: 10 }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="_ops_sk" style={{ width: "52%", height: 18 }} />
            <div className="_ops_sk" style={{ width: 64, height: 30, borderRadius: 10 }} />
          </div>
          <div className="_ops_sk" style={{ width: "42%", height: 13, marginTop: 10 }} />
        </div>
      ))}
    </div>
  );

  return (
    <Stack className="mt-3" gap={2}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", display: "flex", alignItems: "center", gap: 8 }}>
            Active Orders
            {activeOrders.length > 0 && (
              <span style={{ background: "#007AFF", color: "#fff", fontSize: ".72rem", fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>
                {activeOrders.length}
              </span>
            )}
          </div>
          <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", marginTop: 2 }}>
            Orders you've picked for delivery
          </div>
        </div>
        <button className="ops-rfrsh" onClick={handleRefresh} disabled={isRefreshing || isLoading}>
          {isRefreshing ? (
            <><span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(0,122,255,.3)", borderTopColor: "#007AFF", borderRadius: "50%", animation: "_ops_sk_spin .7s linear infinite" }} />Refreshing...</>
          ) : (
            <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/><path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/></svg>Refresh</>
          )}
        </button>
      </div>
      {isLoading ? (
        renderLoadingSkeletons()
      ) : activeOrders.length > 0 ? (
        <Stack gap={3}>
          {activeOrders.map((orderItem) => (
            <ActiveOrderCard
              key={orderItem.id}
              user={user}
              riderId={riderId}
              activeOrder={orderItem}
              orderList={orders}
              setOrderList={setOrders}
            />
          ))}
        </Stack>
      ) : (
        <div style={{ textAlign: "center", padding: "40px 16px", background: "rgba(255,255,255,.88)", border: "1px solid rgba(15,23,42,.06)", borderRadius: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(142,142,147,.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#8E8E93" viewBox="0 0 16 16"><path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5l2.404.961L10.404 2l-2.218-.887zm3.564 1.426L5.596 5 8 5.961 14.154 3.5l-2.404-.961zm3.25 1.7-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923l6.5 2.6zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464L7.443.184z"/></svg>
          </div>
          <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 4 }}>No active orders</div>
          <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.5)" }}>Orders you accept will appear here</div>
        </div>
      )}
    </Stack>
  );
};

const ListView = React.memo(({
  orders,
  setOrders,
  setActiveOrders,
  riderId,
  userLocation,
  stores
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { triggerHaptic } = useMobileFeatures();
  const { token } = useUserContext();

  const filteredOrders = useMemo(() =>
    orders.filter(order => order.paid === true),
    [orders]
  );

  const handleRefresh = useCallback(async () => {
    try {
      await triggerHaptic();
      setIsRefreshing(true);
      const [placedOrders, storesData] = await Promise.all([
        fetchPlacedOrders(token),
        fetchStores(),
      ]);
      setOrders(placedOrders);
      // Note: You'll need to update the stores state in the parent component
    } catch (error) {
      console.error("Error refreshing orders:", error);
      toast.error("Failed to refresh orders");
    } finally {
      setIsRefreshing(false);
    }
  }, [setOrders, token]);

  const ListHeader = ({ count }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", display: "flex", alignItems: "center", gap: 8 }}>
          Available Orders
          {count > 0 && (
            <span style={{ background: "#34C759", color: "#fff", fontSize: ".72rem", fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>
              {count}
            </span>
          )}
        </div>
        <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", marginTop: 2 }}>
          {count > 0 ? `${count} order${count !== 1 ? 's' : ''} ready for pickup` : 'Orders ready for pickup will appear here'}
        </div>
      </div>
      <button className="ops-rfrsh" onClick={handleRefresh} disabled={isRefreshing}>
        {isRefreshing ? (
          <><span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(0,122,255,.3)", borderTopColor: "#007AFF", borderRadius: "50%", animation: "_ops_sk_spin .7s linear infinite" }} />Refreshing...</>
        ) : (
          <><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/><path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/></svg>Refresh</>
        )}
      </button>
    </div>
  );

  if (filteredOrders.length === 0) {
    return (
      <Stack gap={2}>
        <ListHeader count={0} />
        <div style={{ textAlign: "center", padding: "40px 16px", background: "rgba(255,255,255,.88)", border: "1px solid rgba(15,23,42,.06)", borderRadius: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(142,142,147,.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#8E8E93" viewBox="0 0 16 16">
              <path d="M4.98 4a.5.5 0 0 0-.39.188L1.54 8H6a.5.5 0 0 1 .5.5 1.5 1.5 0 1 0 3 0A.5.5 0 0 1 10 8h4.46l-3.05-3.812A.5.5 0 0 0 11.02 4H4.98zm9.54 5H11a.5.5 0 0 1-.5-.5 1.5 1.5 0 0 0-3 0 .5.5 0 0 1-.5.5H1.48l3.05 3.812A.5.5 0 0 0 4.98 12h6.04a.5.5 0 0 0 .39-.188L14.52 9z"/>
            </svg>
          </div>
          <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 4 }}>No orders available</div>
          <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.5)" }}>Check back later for new delivery requests</div>
        </div>
      </Stack>
    );
  }

  return (
    <>
      <Stack gap={2}>
        <ListHeader count={filteredOrders.length} />
        <Stack gap={3}>
          {filteredOrders.map((order) => (
            <OrderListItem
              key={order.id}
              order={order}
              userLocation={userLocation}
              stores={stores}
              orders={orders}
              setOrders={setOrders}
              setActiveOrders={setActiveOrders}
              riderId={riderId}
              onSelect={setSelectedOrder}
            />
          ))}
        </Stack>
      </Stack>

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onHide={() => setSelectedOrder(null)}
          orders={orders}
          setOrders={setOrders}
          setActiveOrders={setActiveOrders}
          riderId={riderId}
          userLocation={userLocation}
        />
      )}
    </>
  );
});

const OrderListItem = React.memo(({ order, userLocation, orders, setOrders, setActiveOrders, riderId, onSelect }) => {
  const [store, setStore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const { triggerHaptic } = useMobileFeatures();
  const { token } = useUserContext();

  useEffect(() => {
    const loadStoreInfo = async () => {
      try {
        const storeData = await fetchStoreInfo(order.cart[0].store);
        setStore(storeData);
      } catch (error) {
        console.error("Error loading store info:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoreInfo();
  }, [order.cart]);

  const updateOrderStatus = async (id) => {
    if (!riderId) {
      toast.info("Session expired, please login.");
      return;
    }

    try {
      await triggerHaptic();
      setBtnLoading(true);
      await acceptOrder(id, riderId, order, token);

      const [placedOrders, activeOrders] = await Promise.all([
        fetchPlacedOrders(token),
        fetchActiveOrders(riderId, token),
      ]);

      setOrders(placedOrders);
      setActiveOrders(activeOrders);
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    } finally {
      setBtnLoading(false);
    }
  };

  const calculateDistance = useCallback((orderLat, orderLng) => {
    if (!userLocation.lat || !userLocation.lng) return null;
    
    const R = 6371; // Earth's radius in km
    const dLat = (orderLat - userLocation.lat) * Math.PI / 180;
    const dLng = (orderLng - userLocation.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(userLocation.lat * Math.PI / 180) * Math.cos(orderLat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance < 1 ? `${(distance * 1000).toFixed(0)}m` : `${distance.toFixed(1)}km`;
  }, [userLocation]);

  const distance = calculateDistance(order.location.lat, order.location.lng);

  return (
    <div className="ops-card" style={{ fontFamily: OPS_FONT, cursor: "pointer" }} onClick={() => onSelect(order)}>
      {/* Row 1: Avatar + Name/Phone | Paid badge + distance */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(0,122,255,.10)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", fontWeight: 700, color: "#007AFF", flexShrink: 0 }}>
            {order.firstName?.charAt(0)?.toUpperCase() || 'C'}
          </div>
          <div>
            <div style={{ fontSize: "0.9375rem", fontWeight: 600, color: "#1c1c1e" }}>
              {order.firstName} {order.lastName}
            </div>
            <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", marginTop: 2 }}>{order.phone}</div>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <span className="ops-pill" style={{ background: "rgba(52,199,89,.12)", color: "#34C759" }}>✓ Paid</span>
          {distance && <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.45)", marginTop: 4 }}>{distance} away</div>}
        </div>
      </div>

      {/* Row 2: Store | Total */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid rgba(60,60,67,.07)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(118,118,128,.10)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="rgba(60,60,67,0.5)" viewBox="0 0 16 16">
              <path d="M2.97 1.35A1 1 0 0 1 3.73 1h8.54a1 1 0 0 1 .76.35l2.609 3.044A1.5 1.5 0 0 1 16 5.37v.255a2.375 2.375 0 0 1-4.25 1.458A2.371 2.371 0 0 1 9.875 8 2.37 2.37 0 0 1 8 7.083 2.37 2.37 0 0 1 6.125 8a2.37 2.37 0 0 1-1.875-.917A2.375 2.375 0 0 1 0 5.625V5.37a1.5 1.5 0 0 1 .361-.976l2.61-3.045zm1.78 4.275a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 1 0 2.75 0V5.37a.5.5 0 0 0-.12-.325L12.27 2H3.73L1.12 5.045A.5.5 0 0 0 1 5.37v.255a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0zM12 6a.5.5 0 0 0-.5.5v3.5a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5V6.5a.5.5 0 0 0-.5-.5h-1z"/>
            </svg>
          </div>
          <span style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.6)" }}>
            {isLoading ? 'Loading...' : store?.store_name || 'Store'}
          </span>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#007AFF" }}>{formatCurrency(order.cost.total)}</div>
          <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.45)" }}>
            {order.cart?.length || 0} item{(order.cart?.length || 0) !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Row 3: Location + Start button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="rgba(60,60,67,0.4)" viewBox="0 0 16 16">
            <path d="M12.166 8.94c-.524 1.395-1.233 2.305-1.884 3.12C9.292 13.92 8.25 14.665 8 15a.5.5 0 0 1-.5-.5c0-.465.326-.882.744-1.244.42-.367.954-.8 1.5-1.328.546-.528 1.1-1.135 1.641-1.757.54-.62.982-1.227 1.281-1.594z"/>
            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
          </svg>
          <span style={{ fontSize: "0.78rem", color: "rgba(60,60,67,0.45)", fontFeatureSettings: '"tnum"' }}>
            {order.location.lat?.toFixed(4)}, {order.location.lng?.toFixed(4)}
          </span>
        </div>
        {btnLoading ? (
          <button className="ops-start" disabled onClick={(e) => e.stopPropagation()}>
            <span style={{ display: "inline-block", width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "_ops_sk_spin .7s linear infinite" }} />
          </button>
        ) : (
          <button className="ops-start" onClick={(e) => { e.stopPropagation(); updateOrderStatus(order.id); }}>
            Start
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
});

const OpsHome = () => {
  const { user, uid, token, claims, isLoading: loading, isAuthenticated } = useUserContext();
  const userLocation = useLocation();
  const routeLocation = useRouteLocation();
  const [orders, setOrders] = useState([]);
  const [stores, setStores] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [viewMode, setViewMode] = useState('map');
  const { triggerHaptic } = useMobileFeatures();
  const navigate = useNavigate();

  const fetchInitialData = useCallback(async () => {
    if (!uid || !token) return;
    try {
      const [placedOrders, storesData] = await Promise.all([
        fetchPlacedOrders(token),
        fetchStores(),
      ]);
      setOrders(placedOrders);
      setStores(storesData);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load initial data");
    }
  }, [uid, token]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const fetchActiveOrdersData = useCallback(async () => {
    if (!uid || !token) return;
    try {
      const activeOrdersData = await fetchActiveOrders(uid, token);
      setActiveOrders(activeOrdersData);
    } catch (error) {
      console.error("Error fetching active orders:", error);
      toast.error("Failed to load active orders");
    }
  }, [uid, token]);

  useEffect(() => {
    fetchActiveOrdersData();
  }, [orders, fetchActiveOrdersData]);

  const refreshOpsData = useCallback(async () => {
    await Promise.all([fetchInitialData(), fetchActiveOrdersData()]);
  }, [fetchInitialData, fetchActiveOrdersData]);

  const handleLoginRedirect = () => goToLogin(navigate, routeLocation);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <SessionErrorCard onLoginClick={handleLoginRedirect} />;
  }

  if (!claims.operations) {
    return <LoadingSpinner />;
  }

  return (
    <PullToRefreshWrapper onRefresh={refreshOpsData}>
    <Seo {...generateNoIndexSEO("Operations Dashboard")} />
    <Stack gap={3} style={{ fontFamily: OPS_FONT }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.01em" }}>
            Operations
          </div>
          <div style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginTop: 2 }}>
            Manage and track delivery orders
          </div>
        </div>
        <div className="ops-seg">
          <button
            className={`ops-seg-btn${viewMode === 'map' ? ' ops-act' : ''}`}
            onClick={() => setViewMode('map')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M12.166 8.94c-.524 1.395-1.233 2.305-1.884 3.12C9.292 13.92 8.25 14.665 8 15a.5.5 0 0 1-.5-.5c0-.465.326-.882.744-1.244.42-.367.954-.8 1.5-1.328.546-.528 1.1-1.135 1.641-1.757.54-.62.982-1.227 1.281-1.594z"/>
              <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
            </svg>
            Map
          </button>
          <button
            className={`ops-seg-btn${viewMode === 'list' ? ' ops-act' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm-3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm0 4a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
            </svg>
            List
          </button>
        </div>
      </div>

      {viewMode === 'map' ? (
        <MapSection
          userLocation={userLocation}
          orders={orders.filter(order => order.paid === true)}
          setOrders={setOrders}
          stores={stores}
          setStores={setStores}
          setActiveOrders={setActiveOrders}
          riderId={uid}
        />
      ) : (
        <ListView
          orders={orders}
          setOrders={setOrders}
          setActiveOrders={setActiveOrders}
          riderId={uid}
          userLocation={userLocation}
          stores={stores}
        />
      )}

      <ActiveOrdersSection
        activeOrders={activeOrders}
        user={user}
        riderId={uid}
        orders={orders}
        setOrders={setOrders}
        setActiveOrders={setActiveOrders}
      />
    </Stack>
    </PullToRefreshWrapper>
  );
};

const ActiveOrderCard = ({
  user,
  riderId,
  activeOrder,
  orderList,
  setOrderList,
}) => {
  const [order, setOrder] = useState();
  const [isShowModal, setIsShowModal] = useState(false);
  const [copyStatus, setCopyStatus] = useState(false);
  const { token } = useUserContext();

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const orderData = await fetchOrderById(activeOrder.id, token);
        setOrder(orderData);
      } catch (error) {
        console.error("Error loading order:", error);
        toast.error("Failed to load order details");
      }
    };
    loadOrder();
  }, [activeOrder.id, token]);

  const onCopyText = () => {
    setCopyStatus(true);
    navigator.clipboard.writeText(activeOrder.phone);
    setTimeout(() => setCopyStatus(false), 2500);
  };

  return (
    <>
      <div className="ops-card" style={{ fontFamily: OPS_FONT }}>
        <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.45)", marginBottom: 8 }}>
          {activeOrder.updatedAt ? `Updated ${new Date(activeOrder.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e" }}>
            {activeOrder.clientName}
          </div>
          <button
            onClick={() => setIsShowModal(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 14px", background: "rgba(0,122,255,.08)", color: "#007AFF", border: "none", borderRadius: 10, fontSize: ".8125rem", fontWeight: 600, cursor: "pointer", fontFamily: OPS_FONT }}
          >
            View
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"/>
            </svg>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.6)" }}>{activeOrder.phone}</span>
          {copyStatus ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#34C759" viewBox="0 0 16 16">
              <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
            </svg>
          ) : (
            <svg onClick={onCopyText} xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="rgba(60,60,67,0.4)" style={{ cursor: "pointer" }} viewBox="0 0 16 16">
              <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1z"/>
              <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0z"/>
            </svg>
          )}
        </div>
      </div>
      <OrderModal
        user={user}
        activeOrder={activeOrder}
        hideModal={() => setIsShowModal(false)}
        isShowModal={isShowModal}
        orderList={orderList}
        setOrderList={setOrderList}
      />
    </>
  );
};

const PoiMarker = ({
  orders,
  setOrders,
  userLocation,
  setActiveOrders,
  riderId,
}) => {
  return (
    <>
      {orders.map((order) => (
        <MarkerContainer
          key={order.id}
          order={order}
          orders={orders}
          setOrders={setOrders}
          userLocation={userLocation}
          setActiveOrders={setActiveOrders}
          riderId={riderId}
        />
      ))}
    </>
  );
};

const StoreMarker = ({ stores, userLocation }) => {
  return (
    <>
      {stores.map((store) => (
        <STRMarkerContainer
          key={store.id}
          store={store}
          location={userLocation}
        />
      ))}
    </>
  );
};

const STRMarkerContainer = ({ store, location }) => {
  const [infoWindowShown, setInfoWindowShown] = useState(false);
  const [copyStatus, setCopyStatus] = useState(false);

  const pinLocation = {
    lat: store.location.latitude,
    lng: store.location.longitude,
  };

  const handleClose = useCallback(() => setInfoWindowShown(false), []);
  const handleClick = useCallback(() => setInfoWindowShown((v) => !v), []);

  const onCopyText = () => {
    const locationText = `${store.location.latitude}, ${store.location.longitude}`;
    setCopyStatus(true);
    navigator.clipboard.writeText(locationText);
    setTimeout(() => setCopyStatus(false), 2500);
  };

  return (
    <>
      <AdvancedMarker
        position={pinLocation}
        clickable={true}
        onClick={handleClick}
      >
        <div style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: "#FBBC04",
          border: "2.5px solid #fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
          cursor: "pointer",
        }} />
      </AdvancedMarker>

      {infoWindowShown && (
        <InfoWindow position={pinLocation} onClose={handleClose}>
          <div style={{ minWidth: 180, fontFamily: OPS_FONT, padding: "4px 2px" }}>
            <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 10 }}>
              {store?.name || store?.store_name || "Store"}
            </div>
            <Button
              className="w-100"
              variant={copyStatus ? "success" : "primary"}
              size="sm"
              onClick={onCopyText}
              disabled={copyStatus}
            >
              {copyStatus ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0" />
                </svg>
              ) : (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1z" />
                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0z" />
                  </svg>
                  Copy location
                </span>
              )}
            </Button>
          </div>
        </InfoWindow>
      )}
    </>
  );
};

const MarkerContainer = ({
  order,
  orders,
  setOrders,
  userLocation,
  setActiveOrders,
  riderId,
}) => {
  const map = useMap();
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleClose = useCallback(() => setInfoWindowShown(false), []);

  const handleClick = useCallback(() => {
    if (!map) return;
    map.panTo(order.location);
    setInfoWindowShown((isShown) => !isShown);
  }, [map, order.location]);

  return (
    <>
      <AdvancedMarker
        position={order.location}
        clickable={true}
        onClick={handleClick}
      >
        <div style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: "#dc3545",
          border: "2.5px solid #fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
          cursor: "pointer",
        }} />
      </AdvancedMarker>
      {infoWindowShown && (
        <InfoWindowUI
          order={order}
          position={order.location}
          close={handleClose}
          orders={orders}
          setOrders={setOrders}
          setActiveOrders={setActiveOrders}
          riderId={riderId}
        />
      )}
    </>
  );
};

const InlineCartItem = ({ id, quantity, selectedOptions }) => {
  const [product, setProduct] = useState(null);
  const [sops, setSops] = useState([]);

  useEffect(() => {
    fetchProduct(id).then(setProduct).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!product) return;
    fetchOptionItems(id, selectedOptions).then(setSops).catch(() => {});
  }, [id, selectedOptions, product]);

  const sopsText = sops.length > 0 ? sops.map((s) => s.value).join(", ") : "";

  return (
    <div style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 8,
      padding: "5px 0",
      borderBottom: "1px solid rgba(60,60,67,0.06)",
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.8125rem", color: "#1c1c1e", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {product ? product.name : "···"}
        </div>
        {sopsText && (
          <div style={{ fontSize: "0.6875rem", color: "rgba(60,60,67,0.45)", marginTop: 1 }}>
            {sopsText}
          </div>
        )}
      </div>
      {quantity > 1 && (
        <span style={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: "#007AFF",
          background: "rgba(0,122,255,0.08)",
          borderRadius: 4,
          padding: "1px 5px",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}>
          ×{quantity}
        </span>
      )}
    </div>
  );
};

const OrderDetailsModal = ({ order, onHide, orders, setOrders, setActiveOrders, riderId, userLocation }) => {
  const [store, setStore] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const { triggerHaptic } = useMobileFeatures();
  const { token } = useUserContext();

  useEffect(() => {
    fetchStoreInfo(order.cart[0].store).then(setStore).catch(() => {});
  }, [order.cart]);

  const distance = (() => {
    if (!userLocation?.lat || !userLocation?.lng) return null;
    const R = 6371;
    const dLat = (order.location.lat - userLocation.lat) * Math.PI / 180;
    const dLng = (order.location.lng - userLocation.lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(userLocation.lat * Math.PI / 180) * Math.cos(order.location.lat * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return d < 1 ? `${(d * 1000).toFixed(0)} m away` : `${d.toFixed(1)} km away`;
  })();

  const handleStart = async () => {
    if (!riderId) { toast.info("Session expired, please login."); return; }
    try {
      await triggerHaptic();
      setBtnLoading(true);
      await acceptOrder(order.id, riderId, order, token);
      const [placed, active] = await Promise.all([fetchPlacedOrders(token), fetchActiveOrders(riderId, token)]);
      setOrders(placed);
      setActiveOrders(active);
      onHide();
    } catch {
      toast.error("Failed to accept order");
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Modal show onHide={onHide} centered scrollable>
      <Modal.Header
        closeButton
        style={{ border: "none", padding: "16px 20px 8px", fontFamily: OPS_FONT }}
      >
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 700, color: "#1c1c1e" }}>
          Order Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ fontFamily: OPS_FONT, padding: "8px 20px 4px" }}>
        {/* Customer */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(0,122,255,.10)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.125rem", fontWeight: 700, color: "#007AFF", flexShrink: 0 }}>
            {order.firstName?.charAt(0)?.toUpperCase() || "C"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "1rem", fontWeight: 700, color: "#1c1c1e" }}>
              {order.firstName} {order.lastName}
            </div>
            <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", marginTop: 2 }}>{order.phone}</div>
          </div>
          <span className="ops-pill" style={{ background: "rgba(52,199,89,.12)", color: "#34C759", flexShrink: 0 }}>
            ✓ Paid
          </span>
        </div>

        {/* Store + Total */}
        <div style={{ background: "rgba(118,118,128,0.06)", borderRadius: 12, padding: "12px 14px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "0.6875rem", color: "rgba(60,60,67,0.45)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>Store</div>
            <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1c1c1e" }}>{store?.store_name || "—"}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.6875rem", color: "rgba(60,60,67,0.45)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>Total</div>
            <div style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#007AFF" }}>{formatCurrency(order.cost.total)}</div>
          </div>
        </div>

        {/* Location */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 0", borderTop: "1px solid rgba(60,60,67,0.07)", borderBottom: "1px solid rgba(60,60,67,0.07)", marginBottom: 16 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="rgba(60,60,67,0.4)" viewBox="0 0 16 16">
            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
          </svg>
          <span style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.5)", fontFeatureSettings: '"tnum"', flex: 1 }}>
            {order.location.lat?.toFixed(5)}, {order.location.lng?.toFixed(5)}
          </span>
          {distance && (
            <span style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.4)", whiteSpace: "nowrap" }}>{distance}</span>
          )}
        </div>

        {/* Cart items */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.6875rem", fontWeight: 600, color: "rgba(60,60,67,0.45)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
            {order.cart?.length || 0} Item{(order.cart?.length || 0) !== 1 ? "s" : ""}
          </div>
          {order.cart?.map((item) => (
            <InlineCartItem key={item.id} {...item} />
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ border: "none", padding: "8px 20px 20px" }}>
        {btnLoading ? (
          <Button variant="primary" className="w-100" disabled>
            <Spinner animation="grow" size="sm" />
          </Button>
        ) : (
          <Button variant="primary" className="w-100" onClick={handleStart}>
            Start delivery
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const InfoWindowUI = ({
  order,
  position,
  close,
  orders,
  setOrders,
  setActiveOrders,
  riderId,
}) => {
  const [store, setStore] = useState();
  const [btnLoading, setBtnLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { triggerHaptic } = useMobileFeatures();
  const { token } = useUserContext();

  useEffect(() => {
    const loadStoreInfo = async () => {
      try {
        const storeData = await fetchStoreInfo(order.cart[0].store);
        setStore(storeData);
      } catch (error) {
        console.error("Error loading store info:", error);
        toast.error("Failed to load store information");
      }
    };

    loadStoreInfo();
  }, [order.cart]);

  const updateOrderStatus = async (id) => {
    if (!riderId) {
      toast.info("Session expired, please login.");
      return;
    }

    try {
      await triggerHaptic();
      setBtnLoading(true);
      await acceptOrder(id, riderId, order, token);

      const [placedOrders, activeOrders] = await Promise.all([
        fetchPlacedOrders(token),
        fetchActiveOrders(riderId, token),
      ]);

      setOrders(placedOrders);
      setActiveOrders(activeOrders);
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <InfoWindow position={position} onClose={close}>
      <div style={{ minWidth: 200, fontFamily: OPS_FONT, padding: "4px 2px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 2 }}>
          <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1c1c1e" }}>
            {order.firstName} {order.lastName}
          </div>
          <button
            onClick={close}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 0 0 8px", color: "rgba(60,60,67,0.4)", fontSize: "1rem", lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)", marginBottom: 10 }}>
          {order.phone}
        </div>
        {store && (
          <div style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.6)", marginBottom: 12 }}>
            <span style={{ color: "rgba(60,60,67,0.45)" }}>Store: </span>
            <span style={{ fontWeight: 600, color: "#1c1c1e" }}>{store.store_name}</span>
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => setCartOpen((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              background: "none",
              border: "none",
              borderTop: "1px solid rgba(60,60,67,0.07)",
              borderBottom: cartOpen ? "none" : "1px solid rgba(60,60,67,0.07)",
              padding: "7px 0",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: "0.8125rem", color: "#007AFF" }}>
              Cart · {order.cart?.length || 0} item{(order.cart?.length || 0) !== 1 ? "s" : ""}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              fill="rgba(60,60,67,0.35)"
              viewBox="0 0 16 16"
              style={{ transform: cartOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.18s ease", flexShrink: 0 }}
            >
              <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
          {cartOpen && (
            <div style={{ paddingBottom: 6, borderBottom: "1px solid rgba(60,60,67,0.07)" }}>
              {order.cart?.map((item) => (
                <InlineCartItem key={item.id} {...item} />
              ))}
            </div>
          )}
        </div>
        {btnLoading ? (
          <Button variant="primary" className="w-100" disabled>
            <Spinner animation="grow" size="sm" />
          </Button>
        ) : (
          <Button variant="primary" className="w-100" onClick={() => updateOrderStatus(order.id)}>
            Start delivery
          </Button>
        )}
      </div>
    </InfoWindow>
  );
};

const OrderModal = ({
  user,
  hideModal,
  isShowModal,
  activeOrder,
  orderList,
  setOrderList,
}) => {
  const [copyStatus, setCopyStatus] = useState(false);
  const [checkedItemsArr] = useState([]);
  const [statusBtnLoading, setStatusBtnLoading] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState(activeOrder.status);
  const [storeInfo, setStoreInfo] = useState(null);
  const [storeInfoLoading, setStoreInfoLoading] = useState(false);
  const navigate = useNavigate();
  const { triggerHaptic } = useMobileFeatures();
  const { token } = useUserContext();

  useEffect(() => {
    console.log(activeOrder);
  }, [activeOrder]);

  useEffect(() => {
    setDeliveryStatus(activeOrder.status);
  }, [activeOrder.status]);

  useEffect(() => {
    if (deliveryStatus === "processing" && activeOrder.store_id) {
      const fetchStoreDetails = async () => {
        try {
          setStoreInfoLoading(true);
          const storeData = await fetchStoreInfo(activeOrder.store_id);
          setStoreInfo(storeData);
        } catch (error) {
          console.error("Error fetching store info:", error);
          toast.error("Failed to load store payment details");
        } finally {
          setStoreInfoLoading(false);
        }
      };
      fetchStoreDetails();
    }
  }, [deliveryStatus, activeOrder.store_id]);

  useEffect(() => {
    if (deliveryStatus === "delivered") {
      const updatedOrderList = orderList.map((order) =>
        order.id === activeOrder.id ? { ...order, status: "delivered" } : order
      );
      setOrderList(updatedOrderList);
    }
  }, [deliveryStatus, activeOrder.id, orderList, setOrderList]);

  const updateActiveOrderStatus = async (status) => {
    try {
      await triggerHaptic();
      setStatusBtnLoading(true);

      const response = await updateActiveStatus(
        status,
        activeOrder.order_id,
        activeOrder.rid,
        activeOrder.id,
        activeOrder.cid,
        token
      );

      if (response === "failed") {
        throw new Error("Failed to update order status");
      }

      const delivery = await fetchActiveDeliveryById(
        activeOrder.id,
        activeOrder.rid,
        token
      );
      setDeliveryStatus(delivery.status);

      const updatedOrderList = orderList.map((order) =>
        order.id === activeOrder.id
          ? { ...order, status: delivery.status }
          : order
      );
      setOrderList(updatedOrderList);
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    } finally {
      setStatusBtnLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      const token = await user.getIdToken();
      navigate("/ops/transaction-progress", {
        state: {
          amount: activeOrder.cost,
          orderId: activeOrder.id,
          storeId: activeOrder.store_id,
          rid: activeOrder.rid,
          token: token,
        },
      });
    } catch (error) {
      console.error("Error getting auth token:", error);
      toast.error("Failed to process payment");
    }
  };

  if (deliveryStatus === "completed") {
    hideModal();
    return null;
  }

  const onCopyText = () => {
    const locationText = `${activeOrder.location.lat}, ${activeOrder.location.lng}`;
    setCopyStatus(true);
    navigator.clipboard.writeText(locationText);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  const STEPS = ["accepted", "processing", "enroute"];
  const stepIdx = STEPS.indexOf(deliveryStatus);

  const nextAction = () => {
    switch (deliveryStatus) {
      case "accepted": updateActiveOrderStatus("processing"); break;
      case "processing": updateActiveOrderStatus("enroute"); break;
      case "enroute": updateActiveOrderStatus("delivered"); break;
      default: break;
    }
  };

  const nextLabel =
    deliveryStatus === "accepted" ? "Proceed to preparation" :
    deliveryStatus === "processing" ? "Proceed to enroute" :
    deliveryStatus === "enroute" ? "Mark as delivered" : null;

  const statusColor =
    deliveryStatus === "delivered" ? "#34C759" :
    deliveryStatus === "processing" ? "#FF9500" : "#007AFF";

  const isProceedDisabled = deliveryStatus === "processing" && !activeOrder.is_paid;

  return (
    <Modal show={isShowModal} onHide={hideModal} centered scrollable dialogClassName="ops-modal">
      <Modal.Body style={{ padding: 0, fontFamily: OPS_FONT, overflowX: "hidden" }}>
        {/* iOS drag handle */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 10, paddingBottom: 2 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(60,60,67,0.18)" }} />
        </div>
        <div style={{ padding: "14px 20px 28px" }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: "0.625rem", fontWeight: 600, color: "rgba(60,60,67,0.4)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 3 }}>Delivery</div>
              <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em" }}>{activeOrder.clientName}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: "0.625rem", fontWeight: 700, letterSpacing: "0.06em", color: statusColor, background: `${statusColor}18`, padding: "4px 10px", borderRadius: 20, textTransform: "uppercase" }}>
                {deliveryStatus}
              </span>
              <button onClick={hideModal} style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(60,60,67,0.08)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(60,60,67,0.5)", flexShrink: 0 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Status pipeline */}
          <div style={{ display: "flex", alignItems: "center", marginBottom: 22 }}>
            {["Accepted", "Processing", "Enroute"].map((label, i) => {
              const done = i <= stepIdx;
              return (
                <React.Fragment key={label}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flex: "0 0 auto" }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", background: done ? "#007AFF" : "transparent", border: done ? "none" : "1.5px solid rgba(60,60,67,0.2)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s" }}>
                      {done && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="#fff" viewBox="0 0 16 16">
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                        </svg>
                      )}
                    </div>
                    <span style={{ fontSize: "0.5625rem", fontWeight: done ? 700 : 500, color: done ? "#007AFF" : "rgba(60,60,67,0.35)", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                      {label}
                    </span>
                  </div>
                  {i < 2 && (
                    <div style={{ flex: 1, height: 1.5, background: i < stepIdx ? "#007AFF" : "rgba(60,60,67,0.1)", margin: "0 6px", marginBottom: 15, transition: "background 0.3s" }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Location row */}
          <div style={{ marginBottom: 14 }}>
            <div className="ops-section-lbl">Location</div>
            <div style={{ background: "rgba(118,118,128,0.06)", borderRadius: 12, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="#007AFF" viewBox="0 0 16 16">
                  <path d="M12.166 8.94c-.524 1.395-1.233 2.305-1.884 3.12C9.292 13.92 8.25 14.665 8 15a.5.5 0 0 1-.5-.5c0-.465.326-.882.744-1.244.42-.367.954-.8 1.5-1.328.546-.528 1.1-1.135 1.641-1.757.54-.62.982-1.227 1.281-1.594z"/>
                  <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                </svg>
                <span style={{ fontSize: "0.8125rem", color: "#1c1c1e", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>
                  {activeOrder.location.lat.toFixed(5)}, {activeOrder.location.lng.toFixed(5)}
                </span>
              </div>
              <button onClick={onCopyText} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: copyStatus ? "rgba(52,199,89,0.1)" : "rgba(0,122,255,0.08)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                {copyStatus ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="#34C759" viewBox="0 0 16 16">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="#007AFF" viewBox="0 0 16 16">
                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1z"/>
                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Address grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
            {[
              { label: "Building / Estate", value: activeOrder.buildingEstateName || "—" },
              { label: "Building Type", value: activeOrder.buildingType || "—" },
              { label: "Unit / Floor", value: activeOrder.unitFloor || "—" },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: "rgba(118,118,128,0.06)", borderRadius: 10, padding: "9px 10px" }}>
                <div style={{ fontSize: "0.5625rem", fontWeight: 600, color: "rgba(60,60,67,0.4)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, lineHeight: 1.2 }}>{label}</div>
                <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1c1c1e", lineHeight: 1.3 }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Cart items */}
          <div style={{ marginBottom: 16 }}>
            <div className="ops-section-lbl">Order Items · {activeOrder.cart?.length}</div>
            <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(60,60,67,0.08)" }}>
              {activeOrder?.cart.map((item, i) => (
                <OrderCartItem
                  key={item.id}
                  {...item}
                  checkedItems={checkedItemsArr}
                  isLast={i === activeOrder.cart.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Store payment (processing only) */}
          {deliveryStatus === "processing" && (
            <div style={{ marginBottom: 16 }}>
              <div className="ops-section-lbl">Store Payment</div>
              {storeInfoLoading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "rgba(118,118,128,0.06)", borderRadius: 12 }}>
                  <Spinner animation="border" size="sm" style={{ color: "#007AFF", width: 14, height: 14, borderWidth: 1.5 }} />
                  <span style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.45)" }}>Loading payment details…</span>
                </div>
              ) : storeInfo ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Store", value: storeInfo.name },
                    { label: "Account Type", value: storeInfo.account_type || "N/A" },
                    { label: "Account No.", value: storeInfo.account },
                    { label: "Reference", value: storeInfo.account_ref === 0 ? "N/A" : storeInfo.account_ref },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ background: "rgba(118,118,128,0.06)", borderRadius: 10, padding: "9px 12px" }}>
                      <div style={{ fontSize: "0.5625rem", fontWeight: 600, color: "rgba(60,60,67,0.4)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
                      <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1c1c1e" }}>{value}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: "12px 14px", background: "rgba(118,118,128,0.06)", borderRadius: 12, fontSize: "0.8125rem", color: "rgba(60,60,67,0.4)", textAlign: "center" }}>
                  No payment details available
                </div>
              )}
            </div>
          )}

          {/* Cost summary */}
          <div style={{ background: "rgba(118,118,128,0.06)", borderRadius: 12, padding: "12px 14px", marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 9, borderBottom: "1px solid rgba(60,60,67,0.07)", marginBottom: 9 }}>
              <span style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)" }}>Subtotal</span>
              <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1c1c1e", fontVariantNumeric: "tabular-nums" }}>KES {activeOrder.cost?.toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)" }}>Delivery fee</span>
              <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1c1c1e", fontVariantNumeric: "tabular-nums" }}>KES {activeOrder.deliveryFee?.toLocaleString()}</span>
            </div>
          </div>

          {/* CTA buttons */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {deliveryStatus === "processing" && !activeOrder.is_paid && (
              <button onClick={handlePayment} style={{ width: "100%", padding: "13px", background: "#007AFF", color: "#fff", border: "none", borderRadius: 12, fontSize: "0.9375rem", fontWeight: 600, cursor: "pointer", fontFamily: OPS_FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z"/>
                  <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1z"/>
                </svg>
                Pay Store
              </button>
            )}
            {deliveryStatus === "delivered" ? (
              <button disabled style={{ width: "100%", padding: "13px", background: "rgba(52,199,89,0.1)", color: "#34C759", border: "1px solid rgba(52,199,89,0.2)", borderRadius: 12, fontSize: "0.9375rem", fontWeight: 600, cursor: "default", fontFamily: OPS_FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                </svg>
                Order Delivered
              </button>
            ) : statusBtnLoading ? (
              <button disabled style={{ width: "100%", padding: "13px", background: "rgba(60,60,67,0.06)", color: "rgba(60,60,67,0.45)", border: "none", borderRadius: 12, fontSize: "0.9375rem", fontWeight: 600, fontFamily: OPS_FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Spinner animation="grow" size="sm" style={{ width: 14, height: 14 }} />
                Updating…
              </button>
            ) : (
              <button onClick={nextAction} disabled={isProceedDisabled} style={{ width: "100%", padding: "13px", background: isProceedDisabled ? "rgba(60,60,67,0.06)" : "rgba(0,122,255,0.09)", color: isProceedDisabled ? "rgba(60,60,67,0.3)" : "#007AFF", border: isProceedDisabled ? "none" : "1px solid rgba(0,122,255,0.18)", borderRadius: 12, fontSize: "0.9375rem", fontWeight: 600, cursor: isProceedDisabled ? "not-allowed" : "pointer", fontFamily: OPS_FONT, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, transition: "opacity 0.15s" }}>
                {nextLabel}
                {!isProceedDisabled && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8"/>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

const OrderCartItem = ({ id, quantity, selectedOptions, checkedItems, isLast }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [sops, setSops] = useState([]);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const productData = await fetchProduct(id);
        setProduct(productData);
      } catch (error) {
        console.error("Error loading product:", error);
        toast.error("Failed to load product details");
      }
    };

    loadProduct();
  }, [id]);

  useEffect(() => {
    const loadOptionItems = async () => {
      try {
        const optionsData = await fetchOptionItems(id, selectedOptions);
        setSops(optionsData);
      } catch (error) {
        console.error("Error loading option items:", error);
        toast.error("Failed to load option items");
      } finally {
        setIsLoading(false);
      }
    };

    if (product) {
      loadOptionItems();
    }
  }, [id, selectedOptions, product]);

  const sopsText = sops.length > 0 ? sops.map((sop) => sop.value).join(", ") : "";

  if (isLoading) {
    return (
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, borderBottom: isLast ? "none" : "1px solid rgba(60,60,67,0.07)" }}>
        <div className="_ops_sk" style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="_ops_sk" style={{ width: "55%", height: 10, marginBottom: 6 }} />
          <div className="_ops_sk" style={{ width: "38%", height: 8 }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderBottom: isLast ? "none" : "1px solid rgba(60,60,67,0.07)", fontFamily: OPS_FONT }}>
      {product?.imageSrc && (
        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "rgba(118,118,128,0.08)" }}>
          <img src={product.imageSrc} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1c1c1e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {product?.name}
        </div>
        {sopsText && (
          <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.45)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {sopsText}
          </div>
        )}
      </div>
      {quantity > 1 && (
        <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "#007AFF", background: "rgba(0,122,255,0.08)", borderRadius: 6, padding: "2px 7px", flexShrink: 0 }}>
          ×{quantity}
        </span>
      )}
    </div>
  );
};


// Styles are injected via the module-level ops-main-styles guard above.

export default OpsHome;
